import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, CalendarDays, ClipboardList, Handshake, Pencil, PlusCircle, UserRound } from 'lucide-react';

import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import AdminToast from '../AdminToast';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import AcademiaSelect from '../../common/AcademiaSelect';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import {
  AcademicPanelCard,
  AcademicPanelListItem,
  AcademicPanelTableWrap,
} from './AcademicPanelCard';
import {
  bulkProposeExcellenceShortlist,
  deleteAdminOpportunite,
  deleteAdminOrientation,
  deleteExcellencePeriode,
  fetchAdminPartenairesSponsors,
  fetchExcellenceAdminOverview,
  fetchExcellenceOpportunites,
  fetchExcellenceOrientations,
  fetchExcellencePartenaires,
  fetchExcellencePeriodesAdmin,
  fetchExcellenceShortlist,
  proposeExcellenceOrientation,
  saveExcellenceOpportunite,
  saveExcellencePeriode,
  setExcellencePartenaireLien,
  seedExcellencePartners,
  updateExcellenceOrientationStatut,
} from '../../../services/academiaAdminService';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import {
  buildDefaultPeriodeOptions,
  buildPeriodeCode,
  formatPeriodeLabel,
  parsePeriodeCode,
} from '../../../utils/excellencePeriodes';
import { isAdministrateur } from '../../../utils/adminAccess';

const OPPORTUNITES_PAGE_SIZE = 6;
const EXCELLENCE_LIST_PAGE_SIZE = 5;

type TabId = 'vue' | 'opportunites' | 'orientations' | 'partenaires' | 'periodes';

const TABS: Array<{ id: TabId; label: string; icon: string }> = [
  { id: 'vue', label: "Vue d'ensemble", icon: 'layout_dashboard' },
  { id: 'periodes', label: 'Périodes', icon: 'calendar_month' },
  { id: 'opportunites', label: 'Opportunités', icon: 'work' },
  { id: 'orientations', label: 'Orientations', icon: 'assignment' },
  { id: 'partenaires', label: 'Partenaires', icon: 'handshake' },
];

const CATEGORIE_LABELS: Record<string, string> = {
  PARTENAIRE: 'Partenaire',
  SPONSOR: 'Sponsor',
};

const TYPES = [
  {
    value: 'STAGE_PROFESSIONNEL',
    label: 'Stage professionnel',
    icon: 'work',
    stripe: 'bg-sky-500',
    iconTile: 'bg-sky-100 text-sky-700 ring-sky-200/60',
    headerGlow: 'from-sky-500/10 via-white to-white',
  },
  {
    value: 'OPPORTUNITE_EMPLOI',
    label: "Opportunité d'emploi",
    icon: 'badge',
    stripe: 'bg-emerald-500',
    iconTile: 'bg-emerald-100 text-emerald-800 ring-emerald-200/60',
    headerGlow: 'from-emerald-500/10 via-white to-white',
  },
  {
    value: 'MENTORAT',
    label: 'Mentorat',
    icon: 'co_present',
    stripe: 'bg-violet-500',
    iconTile: 'bg-violet-100 text-violet-800 ring-violet-200/60',
    headerGlow: 'from-violet-500/10 via-white to-white',
  },
  {
    value: 'BOURSE_PARTIELLE',
    label: 'Bourse partielle',
    icon: 'payments',
    stripe: 'bg-amber-500',
    iconTile: 'bg-amber-100 text-amber-900 ring-amber-200/60',
    headerGlow: 'from-amber-500/12 via-white to-white',
  },
  {
    value: 'FORMATION_AVANCEE',
    label: 'Formation avancée',
    icon: 'school',
    stripe: 'bg-primary',
    iconTile: 'bg-primary/10 text-primary ring-primary/20',
    headerGlow: 'from-primary/12 via-white to-white',
  },
  {
    value: 'AVANTAGE_PARTENAIRE',
    label: 'Avantage partenaire',
    icon: 'handshake',
    stripe: 'bg-teal-500',
    iconTile: 'bg-teal-100 text-teal-800 ring-teal-200/60',
    headerGlow: 'from-teal-500/10 via-white to-white',
  },
] as const;

const FIELD_LABEL = 'text-xs font-bold text-on-surface-variant';
const FIELD_INPUT = 'academia-field mt-1 w-full text-sm text-slate-900';
const FIELD_LABEL_BLOCK = 'block w-full min-w-0 text-sm';
const BTN_PRIMARY = 'academic-panel-btn academic-panel-btn--primary';
const BTN_SECONDARY = 'academic-panel-btn academic-panel-btn--secondary';

const ORIENTATION_STATUS: Record<string, { label: string; className: string }> = {
  PROPOSEE: { label: 'Proposée', className: 'bg-amber-100 text-amber-900 ring-amber-200' },
  ACCEPTEE: { label: 'Acceptée', className: 'bg-emerald-100 text-emerald-900 ring-emerald-200' },
  REFUSEE: { label: 'Refusée', className: 'bg-slate-100 text-slate-700 ring-slate-200' },
  EN_COURS: { label: 'En cours', className: 'bg-sky-100 text-sky-900 ring-sky-200' },
  TERMINEE: { label: 'Terminée', className: 'bg-primary/10 text-primary ring-primary/20' },
};

type OpportuniteRow = {
  id: string;
  titre: string;
  typeLabel: string;
  typeAvantage?: string;
  description?: string;
  partenaire?: { id: string; nom: string };
  reserveShortlist: boolean;
  estActif?: boolean;
};

type ShortlistRow = {
  idProfil: string;
  rang: number | null;
  nomComplet: string;
  matricule: string;
  scoreGlobal: number;
};

type OrientationRow = {
  id: string;
  statut: string;
  apprenant: { nomComplet: string; matricule: string };
  opportunite: { titre: string; typeLabel: string; partenaire: string | null };
};

type PartenaireOppLink = {
  id: string;
  titre: string;
  estActif: boolean;
  periode?: string | null;
};

type PartenaireRow = {
  id: string;
  nom: string;
  categorie?: string;
  urlLogo?: string | null;
  urlSite?: string | null;
  estActif?: boolean;
  lienActif?: boolean;
  nbOpportunites?: number;
  nbOpportunitesActives?: number;
  opportunites?: PartenaireOppLink[];
};

type CatalogPartner = {
  id: string;
  nom: string;
  estActif?: boolean;
  categorie?: string;
};

type PeriodeCatalogRow = {
  code: string;
  libelle?: string | null;
  estActive?: boolean;
  estCourante?: boolean;
  shortlistCount: number;
  orientationsCount: number;
  opportunitesCount: number;
};

type PeriodeFormState = {
  year: number;
  semester: '1' | '2';
  libelle: string;
  estActive: boolean;
  estCourante: boolean;
};

const EMPTY_PERIODE_FORM: PeriodeFormState = {
  year: new Date().getFullYear(),
  semester: '1',
  libelle: '',
  estActive: true,
  estCourante: false,
};

type OpportuniteFormState = {
  typeAvantage: string;
  titre: string;
  description: string;
  idPartenaire: string;
  reserveShortlist: boolean;
};

const EMPTY_OPPORTUNITE_FORM: OpportuniteFormState = {
  typeAvantage: 'STAGE_PROFESSIONNEL',
  titre: '',
  description: '',
  idPartenaire: '',
  reserveShortlist: true,
};

function typeMeta(value?: string) {
  return TYPES.find((t) => t.value === value) ?? TYPES[0];
}

function opportuniteFormFromRow(row: OpportuniteRow): OpportuniteFormState {
  return {
    typeAvantage: row.typeAvantage ?? 'STAGE_PROFESSIONNEL',
    titre: row.titre,
    description: row.description ?? '',
    idPartenaire: row.partenaire?.id ?? '',
    reserveShortlist: row.reserveShortlist,
  };
}

function periodeFormFromRow(row: PeriodeCatalogRow): PeriodeFormState {
  const parsed = parsePeriodeCode(row.code);
  return {
    year: parsed?.year ?? new Date().getFullYear(),
    semester: parsed ? String(parsed.semester) as '1' | '2' : '1',
    libelle: row.libelle ?? '',
    estActive: row.estActive !== false,
    estCourante: row.estCourante === true,
  };
}

function PeriodeFormFields({
  form,
  setForm,
  allowStatusFields = false,
}: {
  form: PeriodeFormState;
  setForm: Dispatch<SetStateAction<PeriodeFormState>>;
  allowStatusFields?: boolean;
}) {
  const previewCode = buildPeriodeCode(form.year, Number(form.semester) as 1 | 2);
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => current + 2 - i);
  }, []);

  return (
    <div className="space-y-4">
      <div className="academic-panel-toolbar academic-panel-toolbar--spread academic-panel-toolbar--pair !gap-4 !p-0">
        <label className={FIELD_LABEL_BLOCK}>
          <span className={FIELD_LABEL}>Année</span>
          <AcademiaSelect
            className="mt-1 w-full"
            value={String(form.year)}
            onChange={(e) => setForm((f) => ({ ...f, year: Number(e.target.value) }))}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </AcademiaSelect>
        </label>
        <label className={FIELD_LABEL_BLOCK}>
          <span className={FIELD_LABEL}>Semestre</span>
          <AcademiaSelect
            className="mt-1 w-full"
            value={form.semester}
            onChange={(e) => setForm((f) => ({ ...f, semester: e.target.value as '1' | '2' }))}
          >
            <option value="1">Semestre 1 (S1)</option>
            <option value="2">Semestre 2 (S2)</option>
          </AcademiaSelect>
        </label>
      </div>
      <p className="rounded-lg border border-dashed border-primary/25 bg-primary/5 px-3 py-2 text-xs text-on-surface-variant">
        Code généré : <strong className="text-slate-900">{previewCode}</strong> — {formatPeriodeLabel(previewCode)}
      </p>
      <label className={FIELD_LABEL_BLOCK}>
        <span className={FIELD_LABEL}>Libellé affiché (optionnel)</span>
        <input
          className={FIELD_INPUT}
          placeholder="Ex. Promotion excellence 2026 — S1"
          value={form.libelle}
          onChange={(e) => setForm((f) => ({ ...f, libelle: e.target.value }))}
        />
      </label>
      {allowStatusFields ? (
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="academia-checkbox"
              checked={form.estActive}
              onChange={(e) => setForm((f) => ({ ...f, estActive: e.target.checked }))}
            />
            <span className="text-sm font-medium text-slate-800">Période active</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="academia-checkbox"
              checked={form.estCourante}
              onChange={(e) => setForm((f) => ({ ...f, estCourante: e.target.checked }))}
            />
            <span className="text-sm font-medium text-slate-800">Période courante par défaut</span>
          </label>
        </div>
      ) : null}
    </div>
  );
}

function TablePaginationFooter({
  pagination,
  itemCount,
}: {
  pagination: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    summary: string;
  };
  itemCount: number;
}) {
  if (itemCount <= 0) return null;
  return (
    <AdminPagination
      className="px-4 py-3"
      currentPage={pagination.currentPage}
      totalPages={pagination.totalPages}
      onPageChange={pagination.onPageChange}
      summary={pagination.summary}
    />
  );
}

function OpportuniteFormFields({
  form,
  setForm,
  partenaires,
}: {
  form: OpportuniteFormState;
  setForm: Dispatch<SetStateAction<OpportuniteFormState>>;
  partenaires: { id: string; nom: string }[];
}) {
  const selectedType = typeMeta(form.typeAvantage);

  return (
    <div className="space-y-4">
      <div className="academic-panel-toolbar academic-panel-toolbar--spread academic-panel-toolbar--pair !gap-4 !p-0">
        <label className={FIELD_LABEL_BLOCK}>
          <span className={FIELD_LABEL}>Type d&apos;avantage</span>
          <AcademiaSelect
            className="mt-1 w-full"
            value={form.typeAvantage}
            onChange={(e) => setForm((f) => ({ ...f, typeAvantage: e.target.value }))}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </AcademiaSelect>
        </label>
        <label className={FIELD_LABEL_BLOCK}>
          <span className={FIELD_LABEL}>Partenaire (catalogue Partenariat)</span>
          <AcademiaSelect
            className="mt-1 w-full"
            value={form.idPartenaire}
            onChange={(e) => setForm((f) => ({ ...f, idPartenaire: e.target.value }))}
          >
            <option value="">— Aucun partenaire —</option>
            {partenaires.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nom}
              </option>
            ))}
          </AcademiaSelect>
        </label>
      </div>
      <label className={FIELD_LABEL_BLOCK}>
        <span className={FIELD_LABEL}>Titre de l&apos;opportunité</span>
        <input
          className={FIELD_INPUT}
          placeholder="Ex. Stage en institution financière"
          value={form.titre}
          onChange={(e) => setForm((f) => ({ ...f, titre: e.target.value }))}
        />
      </label>
      <label className={FIELD_LABEL_BLOCK}>
        <span className={FIELD_LABEL}>Description</span>
        <textarea
          className={`${FIELD_INPUT} min-h-[96px] resize-y`}
          rows={3}
          placeholder="Décrivez les conditions, la durée et le public cible…"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          className="academia-checkbox"
          checked={form.reserveShortlist}
          onChange={(e) => setForm((f) => ({ ...f, reserveShortlist: e.target.checked }))}
        />
        <span className="text-sm font-medium text-slate-800">Réservée à la shortlist excellence</span>
      </label>
      <div className="flex items-center gap-2 rounded-xl border border-dashed border-primary/25 bg-primary/5 px-3 py-2 text-xs text-on-surface-variant">
        <LucideSymbolIcon name={selectedType.icon} tone="primary" size={16} />
        <span>
          Aperçu : <strong className="text-slate-900">{selectedType.label}</strong>
          {form.reserveShortlist ? ' · shortlist uniquement' : ' · tous profils'}
        </span>
      </div>
    </div>
  );
}

function OpportuniteAdminCard({
  row,
  bulkSending,
  onEdit,
  onToggleActive,
  onBulkPropose,
  deleteAction,
}: {
  row: OpportuniteRow;
  bulkSending: boolean;
  onEdit: () => void;
  onToggleActive: () => void;
  onBulkPropose: () => void;
  deleteAction?: React.ReactNode;
}) {
  const meta = typeMeta(row.typeAvantage);
  const isInactive = row.estActif === false;

  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-2xl border shadow-sm transition duration-200 ${
        isInactive
          ? 'border-slate-200/90 bg-slate-50/80 opacity-80'
          : 'border-outline-variant/20 bg-white hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-lg'
      }`}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${isInactive ? 'bg-slate-300' : meta.stripe}`} aria-hidden />

      <div
        className={`border-b border-outline-variant/10 bg-gradient-to-br px-4 pb-4 pt-4 pl-5 ${
          isInactive ? 'from-slate-100/60 to-white' : meta.headerGlow
        }`}
      >
        <div className="flex items-start gap-3">
          <span
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ring-1 ${
              isInactive ? 'bg-slate-200 text-slate-500 ring-slate-300/50' : meta.iconTile
            }`}
          >
            <LucideSymbolIcon name={meta.icon} tone="inherit" size={22} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              {isInactive ? (
                <span className="rounded-md bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-700">
                  Inactive
                </span>
              ) : (
                <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-200/80">
                  Active
                </span>
              )}
              {row.reserveShortlist ? (
                <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900 ring-1 ring-amber-200/80">
                  Shortlist
                </span>
              ) : (
                <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200/80">
                  Tous profils
                </span>
              )}
            </div>
            <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">
              {meta.label}
            </p>
            <h3 className="mt-1 line-clamp-2 text-base font-extrabold leading-snug text-slate-900">{row.titre}</h3>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col px-4 py-4 pl-5">
        <div className="rounded-xl border border-outline-variant/15 bg-slate-50/70 px-3.5 py-3">
          {row.description ? (
            <p className="line-clamp-4 text-xs leading-relaxed text-slate-600">{row.description}</p>
          ) : (
            <p className="text-xs italic text-on-surface-variant">Aucune description renseignée.</p>
          )}
        </div>

        {row.partenaire?.nom ? (
          <div className="mt-3 inline-flex max-w-full items-center gap-2 self-start rounded-full border border-outline-variant/20 bg-white px-3 py-1.5 shadow-sm">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
              <LucideSymbolIcon name="handshake" tone="primary" size={13} />
            </span>
            <span className="truncate text-xs font-bold text-slate-800">{row.partenaire.nom}</span>
          </div>
        ) : (
          <p className="mt-3 text-[11px] font-medium text-on-surface-variant">Sans partenaire associé</p>
        )}
      </div>

      <footer className="mt-auto border-t border-outline-variant/15 bg-slate-50/60 px-3 py-3 pl-4">
        {!isInactive ? (
          <button
            type="button"
            disabled={bulkSending}
            className={`${BTN_PRIMARY} mb-2 w-full !justify-center !py-2 text-xs disabled:opacity-50`}
            onClick={onBulkPropose}
          >
            <LucideSymbolIcon name="send" tone="inherit" size={14} />
            {bulkSending ? 'Envoi à la shortlist…' : 'Proposer à la shortlist'}
          </button>
        ) : null}
        <div className="flex gap-2">
          <button
            type="button"
            className={`${BTN_SECONDARY} flex-1 !justify-center !gap-1.5 !py-2 text-xs`}
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            Modifier
          </button>
          <button
            type="button"
            className={`${BTN_SECONDARY} flex-1 !justify-center !gap-1.5 !py-2 text-xs ${
              isInactive ? '!border-emerald-200 !text-emerald-800 hover:!bg-emerald-50' : ''
            }`}
            onClick={onToggleActive}
          >
            <LucideSymbolIcon
              name={isInactive ? 'check_circle' : 'block'}
              tone="inherit"
              size={14}
            />
            {isInactive ? 'Réactiver' : 'Désactiver'}
          </button>
        </div>
        {deleteAction ? <div className="mt-2">{deleteAction}</div> : null}
      </footer>
    </article>
  );
}

export default function AcademiaExcellenceAdminPanel({ user }: { user?: { role?: string } | null }) {
  const canDelete = isAdministrateur(user);
  const [tab, setTab] = useState<TabId>('vue');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [detachingPartenaireId, setDetachingPartenaireId] = useState<string | null>(null);
  const [deletingPeriodeCode, setDeletingPeriodeCode] = useState<string | null>(null);
  const [periode, setPeriode] = useState(() => `${new Date().getFullYear()}-S1`);
  const [periodeOptions, setPeriodeOptions] = useState<string[]>(buildDefaultPeriodeOptions);
  const [periodeCatalog, setPeriodeCatalog] = useState<PeriodeCatalogRow[]>([]);
  const [overview, setOverview] = useState<Record<string, unknown> | null>(null);
  const [opportunites, setOpportunites] = useState<OpportuniteRow[]>([]);
  const [orientations, setOrientations] = useState<OrientationRow[]>([]);
  const [partenaires, setPartenaires] = useState<PartenaireRow[]>([]);
  const [catalogPartners, setCatalogPartners] = useState<CatalogPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [savingOpp, setSavingOpp] = useState(false);
  const [savingEditOpp, setSavingEditOpp] = useState(false);
  const [savingPeriode, setSavingPeriode] = useState(false);
  const [savingEditPeriode, setSavingEditPeriode] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [bulkId, setBulkId] = useState<string | null>(null);
  const [shortlist, setShortlist] = useState<ShortlistRow[]>([]);
  const [editModalOpp, setEditModalOpp] = useState<OpportuniteRow | null>(null);
  const [editModalPeriode, setEditModalPeriode] = useState<PeriodeCatalogRow | null>(null);

  const [form, setForm] = useState<OpportuniteFormState>(EMPTY_OPPORTUNITE_FORM);
  const [editForm, setEditForm] = useState<OpportuniteFormState>(EMPTY_OPPORTUNITE_FORM);
  const [periodeForm, setPeriodeForm] = useState<PeriodeFormState>(EMPTY_PERIODE_FORM);
  const [editPeriodeForm, setEditPeriodeForm] = useState<PeriodeFormState>(EMPTY_PERIODE_FORM);
  const [orientForm, setOrientForm] = useState({
    idProfilApprenant: '',
    idOpportunite: '',
  });

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const ov = await fetchExcellenceAdminOverview(periode);
      const overviewRow = ov as {
        periode?: string;
        periodes?: string[];
      };
      const resolvedPeriode = overviewRow.periode ?? periode;
      if (overviewRow.periodes?.length) {
        setPeriodeOptions(overviewRow.periodes);
        if (resolvedPeriode !== periode) {
          setPeriode(resolvedPeriode);
        }
      }
      const [opp, ori, part, sl, periodesAdmin, catalog] = await Promise.all([
        fetchExcellenceOpportunites(resolvedPeriode),
        fetchExcellenceOrientations(resolvedPeriode),
        fetchExcellencePartenaires({ includeInactive: true }),
        fetchExcellenceShortlist(resolvedPeriode),
        fetchExcellencePeriodesAdmin(),
        fetchAdminPartenairesSponsors().catch(() => []),
      ]);
      setOverview(ov as Record<string, unknown>);
      setOpportunites((opp as OpportuniteRow[]) ?? []);
      setOrientations((ori as OrientationRow[]) ?? []);
      setPartenaires((part as PartenaireRow[]) ?? []);
      setCatalogPartners((catalog as CatalogPartner[]) ?? []);
      setShortlist((sl as ShortlistRow[]) ?? []);
      const periodeRows = (periodesAdmin as PeriodeCatalogRow[]) ?? [];
      setPeriodeCatalog(periodeRows);
      const activeCodes = periodeRows.filter((p) => p.estActive !== false).map((p) => p.code);
      if (activeCodes.length > 0) {
        setPeriodeOptions(activeCodes);
        if (!activeCodes.includes(periode) && !activeCodes.includes(resolvedPeriode)) {
          const courante = periodeRows.find((p) => p.estCourante)?.code ?? activeCodes[0];
          if (courante) setPeriode(courante);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur chargement excellence');
    } finally {
      setLoading(false);
    }
  }, [periode]);

  useEffect(() => {
    void load();
  }, [load]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const runAction = useCallback(
    async (
      action: () => Promise<void>,
      successMessage: string,
      errorFallback = 'Action impossible.',
      options?: { reload?: boolean },
    ) => {
      try {
        await action();
        showToast(successMessage, 'success');
        if (options?.reload !== false) {
          await load();
        }
      } catch (e) {
        showToast(e instanceof Error ? e.message : errorFallback, 'error');
      }
    },
    [load, showToast],
  );

  const runDelete = useCallback(
    async (action: () => Promise<void>, successMessage: string, errorFallback = 'Suppression impossible.') => {
      try {
        await action();
        showToast(successMessage, 'success');
        await load();
      } catch (e) {
        const message = e instanceof Error ? e.message : errorFallback;
        showToast(message, 'error');
        throw new Error(message);
      }
    },
    [load, showToast],
  );

  const s = overview as {
    shortlistCount?: number;
    orientationsCount?: number;
    opportunitesCount?: number;
    topClassement?: { rang: number | null; nomComplet: string; scoreGlobal: number }[];
  } | null;

  const resetOppForm = () => {
    setForm(EMPTY_OPPORTUNITE_FORM);
  };

  const openEditModal = (row: OpportuniteRow) => {
    setEditModalOpp(row);
    setEditForm(opportuniteFormFromRow(row));
  };

  const closeEditModal = useCallback(() => {
    setEditModalOpp(null);
    setEditForm(EMPTY_OPPORTUNITE_FORM);
  }, []);

  const closeEditPeriodeModal = useCallback(() => {
    setEditModalPeriode(null);
    setEditPeriodeForm(EMPTY_PERIODE_FORM);
  }, []);

  const openEditPeriodeModal = (row: PeriodeCatalogRow) => {
    setEditModalPeriode(row);
    setEditPeriodeForm(periodeFormFromRow(row));
  };

  const resetPeriodeForm = () => {
    setPeriodeForm(EMPTY_PERIODE_FORM);
  };

  useEffect(() => {
    if (!editModalOpp) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeEditModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editModalOpp, closeEditModal]);

  useEffect(() => {
    if (!editModalPeriode) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeEditPeriodeModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editModalPeriode, closeEditPeriodeModal]);

  const partnerOptionsForForm = useMemo(() => {
    const byId = new Map<string, { id: string; nom: string }>();
    for (const p of catalogPartners) {
      if (p.estActif !== false) byId.set(p.id, { id: p.id, nom: p.nom });
    }
    // Toujours inclure le partenaire déjà choisi même s’il est inactif ou hors catalogue filtrée.
    for (const id of [form.idPartenaire, editForm.idPartenaire]) {
      if (!id || byId.has(id)) continue;
      const fromLinked = partenaires.find((p) => p.id === id);
      const fromCatalog = catalogPartners.find((p) => p.id === id);
      const nom = fromLinked?.nom ?? fromCatalog?.nom;
      if (nom) byId.set(id, { id, nom });
    }
    return Array.from(byId.values()).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  }, [catalogPartners, partenaires, form.idPartenaire, editForm.idPartenaire]);

  const linkedPartenairesActifs = partenaires.filter((p) => p.lienActif !== false);
  const activeOpportunites = opportunites.filter((o) => o.estActif !== false);
  const inactiveOpportunites = opportunites.filter((o) => o.estActif === false);
  const opportunitesPagination = useAdminTablePagination(opportunites, OPPORTUNITES_PAGE_SIZE);
  const periodesPagination = useAdminTablePagination(periodeCatalog, EXCELLENCE_LIST_PAGE_SIZE);
  const orientationsPagination = useAdminTablePagination(orientations, EXCELLENCE_LIST_PAGE_SIZE);
  const partenairesPagination = useAdminTablePagination(partenaires, EXCELLENCE_LIST_PAGE_SIZE);
  const shortlistPagination = useAdminTablePagination(shortlist, EXCELLENCE_LIST_PAGE_SIZE);

  const periodeToolbar = (
    <div className="academic-panel-toolbar academic-panel-toolbar--spread !rounded-2xl !border !border-outline-variant/25 !shadow-sm">
      <label className={FIELD_LABEL_BLOCK}>
        <span className={FIELD_LABEL}>Période</span>
        <AcademiaSelect
          className="mt-1 w-full"
          value={periode}
          onChange={(e) => setPeriode(e.target.value)}
          aria-label="Période académique"
        >
          {periodeOptions.map((p) => (
            <option key={p} value={p}>
              {formatPeriodeLabel(p)}
            </option>
          ))}
        </AcademiaSelect>
      </label>
    </div>
  );

  return (
    <AdminPageShell
      title="Excellence & employabilité"
      description="Détection des meilleurs apprenants, orientations partenaires et avantages."
      sideCard={{
        label: 'Shortlist',
        value: loading ? '…' : String(s?.shortlistCount ?? 0),
        helper: s ? `${s.orientationsCount ?? 0} orientation(s) · ${s.opportunitesCount ?? 0} opportunité(s)` : undefined,
        sideLinks: [
          { label: 'Tableau public', to: '/excellence', icon: 'emoji_events' },
          { label: 'Académique', to: '/admin/academique', icon: 'menu_book' },
        ],
      }}
      isLoading={loading}
      isError={Boolean(error)}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement de l'excellence…"
    >
      {overview ? (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`academic-panel-btn ${tab === t.id ? 'academic-panel-btn--primary' : 'academic-panel-btn--secondary'}`}
              >
                <LucideSymbolIcon name={t.icon} tone="inherit" size={16} />
                {t.label}
              </button>
            ))}
          </div>

          {tab !== 'partenaires' && tab !== 'periodes' ? periodeToolbar : null}

          {tab === 'periodes' ? (
            <>
              <AcademicPanelCard
                variant="accent"
                icon={<CalendarDays className="h-5 w-5" aria-hidden />}
                title="Nouvelle période"
                subtitle="Format automatique : année + semestre (ex. 2026-S1)"
                footer={
                  <button
                    type="button"
                    disabled={savingPeriode}
                    className={`${BTN_PRIMARY} disabled:opacity-50`}
                    onClick={async () => {
                      setSavingPeriode(true);
                      try {
                        const code = buildPeriodeCode(
                          periodeForm.year,
                          Number(periodeForm.semester) as 1 | 2,
                        );
                        await runAction(
                          async () => {
                            await saveExcellencePeriode({
                              code,
                              libelle: periodeForm.libelle || null,
                            });
                            resetPeriodeForm();
                          },
                          'Période créée avec succès.',
                          'Création impossible.',
                        );
                      } finally {
                        setSavingPeriode(false);
                      }
                    }}
                  >
                    {savingPeriode ? 'Enregistrement…' : 'Ajouter la période'}
                  </button>
                }
              >
                <div className="academic-panel-card__body">
                  <PeriodeFormFields form={periodeForm} setForm={setPeriodeForm} />
                </div>
              </AcademicPanelCard>

              <AcademicPanelCard
                icon={<LucideSymbolIcon name="calendar_month" tone="primary" size={22} />}
                title="Périodes du programme excellence"
                subtitle="Shortlist, orientations et opportunités sont rattachées à une période"
                badge={
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                    {periodeCatalog.length}
                  </span>
                }
                flush
              >
                <AcademicPanelTableWrap
                  empty={
                    periodeCatalog.length === 0 ? (
                      <div className="px-6 py-12 text-center">
                        <LucideSymbolIcon name="calendar_month" tone="muted" size={32} className="mx-auto opacity-60" />
                        <p className="mt-3 text-sm font-semibold text-slate-800">Aucune période</p>
                        <p className="mt-1 text-xs text-on-surface-variant">
                          Créez une période pour structurer le programme excellence.
                        </p>
                      </div>
                    ) : undefined
                  }
                  footer={
                    <TablePaginationFooter pagination={periodesPagination} itemCount={periodeCatalog.length} />
                  }
                >
                  <ul className="divide-y divide-outline-variant/15">
                    {periodesPagination.paginatedItems.map((row) => {
                      const inactive = row.estActive === false;
                      const linked =
                        row.shortlistCount + row.orientationsCount + row.opportunitesCount;
                      return (
                        <AcademicPanelListItem key={row.code}>
                          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-bold text-slate-900">{formatPeriodeLabel(row.code)}</p>
                                <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-700">
                                  {row.code}
                                </span>
                                {row.estCourante ? (
                                  <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">
                                    Courante
                                  </span>
                                ) : null}
                                <span
                                  className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${
                                    inactive
                                      ? 'bg-slate-200 text-slate-700'
                                      : 'bg-emerald-50 text-emerald-800'
                                  }`}
                                >
                                  {inactive ? 'Inactive' : 'Active'}
                                </span>
                              </div>
                              {row.libelle?.trim() ? (
                                <p className="mt-1 text-xs text-on-surface-variant">{row.libelle}</p>
                              ) : null}
                              <p className="mt-1 text-xs text-on-surface-variant">
                                {row.shortlistCount} shortlist · {row.orientationsCount} orientation(s) ·{' '}
                                {row.opportunitesCount} opportunité(s)
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                className={`${BTN_SECONDARY} !px-3 !py-1.5 text-xs`}
                                onClick={() => {
                                  setPeriode(row.code);
                                  setTab('vue');
                                }}
                              >
                                Consulter
                              </button>
                              <button
                                type="button"
                                className={`${BTN_SECONDARY} !px-3 !py-1.5 text-xs`}
                                onClick={() => openEditPeriodeModal(row)}
                              >
                                <Pencil className="h-3.5 w-3.5" aria-hidden />
                                Modifier
                              </button>
                              <button
                                type="button"
                                className={`${BTN_SECONDARY} !px-3 !py-1.5 text-xs`}
                                onClick={() =>
                                  void runAction(
                                    () =>
                                      saveExcellencePeriode(
                                        {
                                          estActive: row.estActive === false,
                                        },
                                        row.code,
                                      ),
                                    row.estActive === false ? 'Période réactivée.' : 'Période désactivée.',
                                    'Mise à jour impossible.',
                                  )
                                }
                              >
                                {row.estActive === false ? 'Réactiver' : 'Désactiver'}
                              </button>
                              <button
                                type="button"
                                className={`${BTN_SECONDARY} !px-3 !py-1.5 text-xs`}
                                disabled={row.estCourante === true}
                                onClick={() =>
                                  void runAction(
                                    () =>
                                      saveExcellencePeriode(
                                        { estCourante: true, estActive: true },
                                        row.code,
                                      ),
                                    'Période définie comme courante.',
                                    'Mise à jour impossible.',
                                  )
                                }
                              >
                                Définir courante
                              </button>
                              <AdminTableDeleteButton
                                canDelete={canDelete}
                                disabled={deletingPeriodeCode === row.code || linked > 0}
                                confirmMessage={`Supprimer la période ${row.code} ?`}
                                onDelete={async () => {
                                  setDeletingPeriodeCode(row.code);
                                  try {
                                    await runDelete(
                                      async () => {
                                        await deleteExcellencePeriode(row.code);
                                      },
                                      'Période supprimée.',
                                      'Suppression impossible.',
                                    );
                                  } finally {
                                    setDeletingPeriodeCode(null);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </AcademicPanelListItem>
                      );
                    })}
                  </ul>
                </AcademicPanelTableWrap>
              </AcademicPanelCard>
            </>
          ) : null}

          {tab === 'vue' ? (
            <>
          <section className="grid gap-3 sm:grid-cols-3">
            <article className="rounded-xl border border-outline-variant/25 bg-gradient-to-br from-white to-surface-container-low p-4 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-on-surface-variant">Shortlist</p>
              <p className="mt-1 text-3xl font-black text-primary">{s?.shortlistCount ?? 0}</p>
            </article>
            <article className="rounded-xl border border-sky-200/50 bg-gradient-to-br from-sky-50/80 to-white p-4 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-sky-800">Orientations</p>
              <p className="mt-1 text-3xl font-black text-sky-900">{s?.orientationsCount ?? 0}</p>
            </article>
            <article className="rounded-xl border border-emerald-200/50 bg-gradient-to-br from-emerald-50/70 to-white p-4 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">Opportunités</p>
              <p className="mt-1 text-3xl font-black text-emerald-900">{s?.opportunitesCount ?? 0}</p>
            </article>
          </section>

          {(s?.topClassement ?? []).length > 0 ? (
            <AcademicPanelCard
              icon={<LucideSymbolIcon name="emoji_events" tone="primary" size={22} />}
              title="Top 5 classement"
              subtitle="Meilleurs profils de la période sélectionnée"
              variant="success"
            >
              <ol className="space-y-2">
                {(s?.topClassement ?? []).map((row) => (
                  <li
                    key={`${row.rang}-${row.nomComplet}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-outline-variant/20 bg-surface-container-lowest px-4 py-3 text-sm"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-black text-primary">
                        {row.rang}
                      </span>
                      <span className="font-semibold text-slate-900">{row.nomComplet}</span>
                    </span>
                    <span className="rounded-lg bg-white px-3 py-1 font-black text-slate-900 shadow-sm">
                      {row.scoreGlobal}
                    </span>
                  </li>
                ))}
              </ol>
            </AcademicPanelCard>
          ) : null}

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="groups" tone="primary" size={22} />}
            title="Shortlist de la période"
            subtitle={`${shortlist.length} apprenant(s) retenu(s) — ${formatPeriodeLabel(periode)}`}
            badge={
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                {shortlist.length}
              </span>
            }
            flush
          >
            <AcademicPanelTableWrap
              empty={
                shortlist.length === 0 ? (
                  <div className="px-6 py-10 text-center">
                    <LucideSymbolIcon name="groups" tone="muted" size={32} className="mx-auto opacity-60" />
                    <p className="mt-3 text-sm font-semibold text-slate-800">Aucun apprenant en shortlist</p>
                    <p className="mt-1 text-xs text-on-surface-variant">
                      Lancez une délibération académique pour cette période.
                    </p>
                  </div>
                ) : undefined
              }
              footer={<TablePaginationFooter pagination={shortlistPagination} itemCount={shortlist.length} />}
            >
              <ul className="divide-y divide-outline-variant/15">
                {shortlistPagination.paginatedItems.map((row) => (
                  <AcademicPanelListItem key={row.idProfil}>
                    <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                      <span className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-black text-primary">
                          {row.rang ?? '—'}
                        </span>
                        <span>
                          <span className="font-semibold text-slate-900">{row.nomComplet}</span>
                          <span className="mt-0.5 block text-xs text-on-surface-variant">
                            {row.matricule}
                          </span>
                        </span>
                      </span>
                      <span className="rounded-lg bg-surface-container-low px-3 py-1 font-black text-slate-900">
                        {row.scoreGlobal} pts
                      </span>
                    </div>
                  </AcademicPanelListItem>
                ))}
              </ul>
            </AcademicPanelTableWrap>
          </AcademicPanelCard>
            </>
          ) : null}

          {tab === 'opportunites' ? (
            <>
          <AcademicPanelCard
            variant="accent"
            flush
            icon={<PlusCircle className="h-5 w-5" aria-hidden />}
            title="Nouvelle opportunité employabilité"
            subtitle="Stages, emploi, mentorat, bourses ou formations avancées pour la shortlist"
            footer={
              <button
                type="button"
                disabled={savingOpp || !form.titre.trim() || !form.description.trim()}
                className={`${BTN_PRIMARY} disabled:opacity-50`}
                onClick={async () => {
                  setSavingOpp(true);
                  try {
                    await runAction(
                      async () => {
                        await saveExcellenceOpportunite({
                          ...form,
                          periode,
                          idPartenaire: form.idPartenaire || null,
                          estActif: true,
                        });
                        resetOppForm();
                      },
                      'Opportunité créée avec succès.',
                      'Création impossible.',
                    );
                  } finally {
                    setSavingOpp(false);
                  }
                }}
              >
                {savingOpp ? 'Enregistrement…' : "Créer l'opportunité"}
              </button>
            }
          >
            <div className="academic-panel-card__body">
              <OpportuniteFormFields form={form} setForm={setForm} partenaires={partnerOptionsForForm} />
            </div>
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<Briefcase className="h-5 w-5" aria-hidden />}
            title="Opportunités"
            subtitle={`${activeOpportunites.length} actif(s) · ${inactiveOpportunites.length} inactif(s) — ${periode}`}
            badge={
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                {activeOpportunites.length}
              </span>
            }
            flush
          >
            <AcademicPanelTableWrap
              empty={
                opportunites.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <LucideSymbolIcon name="work" tone="muted" size={32} className="mx-auto opacity-60" />
                    <p className="mt-3 text-sm font-semibold text-slate-800">Aucune opportunité</p>
                    <p className="mt-1 text-xs text-on-surface-variant">
                      Créez une opportunité et associez un partenaire du catalogue Partenariat.
                    </p>
                  </div>
                ) : undefined
              }
              footer={
                <TablePaginationFooter pagination={opportunitesPagination} itemCount={opportunites.length} />
              }
            >
              <div className="grid gap-5 p-5 sm:grid-cols-2 xl:grid-cols-3">
                {opportunitesPagination.paginatedItems.map((row) => (
                  <OpportuniteAdminCard
                    key={row.id}
                    row={row}
                    bulkSending={bulkId === row.id}
                    onEdit={() => openEditModal(row)}
                    onBulkPropose={async () => {
                      setBulkId(row.id);
                      try {
                        await runAction(
                          () => bulkProposeExcellenceShortlist({ idOpportunite: row.id, periode }),
                          'Propositions envoyées à toute la shortlist (notification + e-mail).',
                          'Envoi à la shortlist impossible.',
                        );
                      } finally {
                        setBulkId(null);
                      }
                    }}
                    onToggleActive={() =>
                      void runAction(
                        () =>
                          saveExcellenceOpportunite(
                            {
                              typeAvantage: row.typeAvantage ?? 'STAGE_PROFESSIONNEL',
                              titre: row.titre,
                              description: row.description ?? '',
                              idPartenaire: row.partenaire?.id ?? null,
                              periode,
                              reserveShortlist: row.reserveShortlist,
                              estActif: row.estActif === false,
                            },
                            row.id,
                          ),
                        row.estActif === false ? 'Opportunité réactivée.' : 'Opportunité désactivée.',
                        'Mise à jour impossible.',
                      )
                    }
                    deleteAction={
                      <AdminTableDeleteButton
                        canDelete={canDelete}
                        disabled={deletingId === row.id}
                        className="w-full !justify-center"
                        confirmMessage={`Supprimer définitivement l'opportunité « ${row.titre} » ? Cette action est irréversible.`}
                        onDelete={async () => {
                          setDeletingId(row.id);
                          try {
                            await runDelete(
                              async () => {
                                await deleteAdminOpportunite(row.id);
                              },
                              'Opportunité supprimée.',
                              'Suppression impossible.',
                            );
                          } finally {
                            setDeletingId(null);
                          }
                        }}
                      />
                    }
                  />
                ))}
              </div>
            </AcademicPanelTableWrap>
          </AcademicPanelCard>
            </>
          ) : null}

          {tab === 'orientations' ? (
            <div className="grid gap-6 xl:grid-cols-2">
            <AcademicPanelCard
              variant="preview"
              icon={<UserRound className="h-5 w-5" aria-hidden />}
              title="Orientation individuelle"
              subtitle={`Proposer une opportunité à un apprenant de la shortlist (${shortlist.length})`}
              footer={
                <button
                  type="button"
                  disabled={proposing || !orientForm.idProfilApprenant.trim() || !orientForm.idOpportunite}
                  className={`${BTN_PRIMARY} disabled:opacity-50`}
                  onClick={async () => {
                    setProposing(true);
                    try {
                      await runAction(
                        async () => {
                          await proposeExcellenceOrientation({ ...orientForm, periode });
                          setOrientForm({ idProfilApprenant: '', idOpportunite: '' });
                        },
                        "Orientation proposée (notification + e-mail envoyés).",
                        'Proposition impossible.',
                      );
                    } finally {
                      setProposing(false);
                    }
                  }}
                >
                  {proposing ? 'Envoi…' : 'Proposer cette orientation'}
                </button>
              }
            >
              <div className="space-y-4">
                <label className={FIELD_LABEL_BLOCK}>
                  <span className={FIELD_LABEL}>Apprenant (shortlist)</span>
                  <AcademiaSelect
                    className="mt-1 w-full"
                    value={orientForm.idProfilApprenant}
                    onChange={(e) => setOrientForm((f) => ({ ...f, idProfilApprenant: e.target.value }))}
                  >
                    <option value="">— Choisir un apprenant —</option>
                    {shortlist.map((row) => (
                      <option key={row.idProfil} value={row.idProfil}>
                        #{row.rang ?? '—'} · {row.nomComplet} ({row.matricule}) — {row.scoreGlobal} pts
                      </option>
                    ))}
                  </AcademiaSelect>
                </label>
                {shortlist.length === 0 ? (
                  <p className="text-xs text-amber-800">
                    Aucun apprenant en shortlist pour {periode}. Lancez une délibération académique d&apos;abord.
                  </p>
                ) : null}
                <label className={FIELD_LABEL_BLOCK}>
                  <span className={FIELD_LABEL}>Opportunité</span>
                  <AcademiaSelect
                    className="mt-1 w-full"
                    value={orientForm.idOpportunite}
                    onChange={(e) => setOrientForm((f) => ({ ...f, idOpportunite: e.target.value }))}
                  >
                    <option value="">— Choisir une opportunité —</option>
                    {activeOpportunites.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.titre}
                      </option>
                    ))}
                  </AcademiaSelect>
                </label>
                <p className="rounded-lg bg-surface-container-low px-3 py-2 text-xs text-on-surface-variant">
                  L&apos;apprenant recevra la proposition dans son espace{' '}
                  <strong className="text-slate-800">Excellence & employabilité</strong> et pourra l&apos;accepter
                  ou la refuser.
                </p>
              </div>
            </AcademicPanelCard>

            <AcademicPanelCard
              variant="success"
              icon={<ClipboardList className="h-5 w-5" aria-hidden />}
              title="Orientations en cours"
              subtitle="Suivi des propositions et validations apprenants"
              badge={
                <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-900">
                  {orientations.length}
                </span>
              }
              flush
            >
              <AcademicPanelTableWrap
                empty={
                  orientations.length === 0 ? (
                    <div className="px-6 py-10 text-center">
                      <LucideSymbolIcon name="assignment" tone="muted" size={32} className="mx-auto opacity-60" />
                      <p className="mt-3 text-sm font-semibold text-slate-800">Aucune orientation</p>
                      <p className="mt-1 text-xs text-on-surface-variant">
                        Proposez une opportunité à la shortlist ou à un apprenant individuel.
                      </p>
                    </div>
                  ) : undefined
                }
                footer={
                  <TablePaginationFooter pagination={orientationsPagination} itemCount={orientations.length} />
                }
              >
                <ul className="divide-y divide-outline-variant/15">
                  {orientationsPagination.paginatedItems.map((row) => {
                    const status = ORIENTATION_STATUS[row.statut] ?? {
                      label: row.statut,
                      className: 'bg-slate-100 text-slate-700 ring-slate-200',
                    };
                    const initials = row.apprenant.nomComplet
                      .split(' ')
                      .map((w) => w[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase();
                    return (
                      <AcademicPanelListItem key={row.id}>
                        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
                          <div className="flex min-w-0 items-start gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-black text-primary">
                              {initials || '?'}
                            </span>
                            <div className="min-w-0">
                              <p className="font-bold text-slate-900">{row.apprenant.nomComplet}</p>
                              <p className="text-xs text-on-surface-variant">
                                {row.apprenant.matricule} · {row.opportunite.titre}
                              </p>
                              <p className="mt-1 text-xs text-on-surface-variant">
                                {row.opportunite.typeLabel}
                                {row.opportunite.partenaire ? ` · ${row.opportunite.partenaire}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset ${status.className}`}
                            >
                              {status.label}
                            </span>
                            {row.statut === 'ACCEPTEE' ? (
                              <button
                                type="button"
                                className={`${BTN_SECONDARY} !px-3 !py-1.5 text-xs`}
                                onClick={() =>
                                  void runAction(
                                    () => updateExcellenceOrientationStatut(row.id, { statut: 'EN_COURS' }),
                                    'Orientation marquée en cours.',
                                    'Mise à jour impossible.',
                                  )
                                }
                              >
                                Marquer en cours
                              </button>
                            ) : null}
                            {row.statut === 'EN_COURS' ? (
                              <button
                                type="button"
                                className={`${BTN_PRIMARY} !px-3 !py-1.5 text-xs`}
                                onClick={() =>
                                  void runAction(
                                    () => updateExcellenceOrientationStatut(row.id, { statut: 'TERMINEE' }),
                                    'Orientation marquée comme terminée.',
                                    'Mise à jour impossible.',
                                  )
                                }
                              >
                                Terminer
                              </button>
                            ) : null}
                            <AdminTableDeleteButton
                              canDelete={canDelete}
                              disabled={deletingId === row.id}
                              confirmMessage={`Supprimer définitivement l'orientation de ${row.apprenant.nomComplet} ? Cette action est irréversible.`}
                              onDelete={async () => {
                                setDeletingId(row.id);
                                try {
                                  await runDelete(
                                    async () => {
                                      await deleteAdminOrientation(row.id);
                                    },
                                    'Orientation supprimée.',
                                    'Suppression impossible.',
                                  );
                                } finally {
                                  setDeletingId(null);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </AcademicPanelListItem>
                    );
                  })}
                </ul>
              </AcademicPanelTableWrap>
            </AcademicPanelCard>
          </div>
          ) : null}

          {tab === 'partenaires' ? (
            <AcademicPanelCard
              icon={<Handshake className="h-5 w-5" aria-hidden />}
              title="Partenaires liés aux opportunités"
              subtitle="Provenance : catalogue Partenariat. Associez un partenaire via une opportunité employabilité."
              badge={
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                  {partenaires.length}
                </span>
              }
              footer={
                <div className="flex flex-wrap gap-2">
                  <Link to="/admin/partenaires-sponsors" className={BTN_SECONDARY}>
                    <LucideSymbolIcon name="handshake" tone="inherit" size={16} />
                    Ouvrir Partenariat
                  </Link>
                  <button
                    type="button"
                    className={BTN_SECONDARY}
                    onClick={() =>
                      void runAction(
                        () => seedExcellencePartners(),
                        'Opportunités de démonstration initialisées (partenaires catalogue uniquement).',
                        'Initialisation impossible.',
                      )
                    }
                  >
                    <LucideSymbolIcon name="refresh" tone="inherit" size={16} />
                    Initialiser les opportunités démo
                  </button>
                </div>
              }
              flush
            >
              <div className="border-b border-outline-variant/15 bg-slate-50/60 px-5 py-3 text-xs text-on-surface-variant">
                {linkedPartenairesActifs.length} partenaire(s) avec au moins une opportunité active · créez / gérez
                les fiches dans{' '}
                <Link to="/admin/partenaires-sponsors" className="font-semibold text-primary underline-offset-2 hover:underline">
                  Partenariat
                </Link>
                .
              </div>
              <AcademicPanelTableWrap
                empty={
                  partenaires.length === 0 ? (
                    <div className="px-6 py-12 text-center">
                      <LucideSymbolIcon name="handshake" tone="muted" size={32} className="mx-auto opacity-60" />
                      <p className="mt-3 text-sm font-semibold text-slate-800">Aucun partenaire lié</p>
                      <p className="mt-1 text-xs text-on-surface-variant">
                        Créez un partenaire dans Partenariat, puis associez-le à une opportunité.
                      </p>
                    </div>
                  ) : undefined
                }
                footer={
                  <TablePaginationFooter pagination={partenairesPagination} itemCount={partenaires.length} />
                }
              >
                <ul className="divide-y divide-outline-variant/15">
                  {partenairesPagination.paginatedItems.map((row) => {
                    const inactiveLink = row.lienActif === false;
                    const categorieLabel = CATEGORIE_LABELS[row.categorie ?? ''] ?? row.categorie ?? 'Partenaire';
                    const oppTitles = (row.opportunites ?? [])
                      .slice(0, 3)
                      .map((o) => o.titre)
                      .join(' · ');
                    return (
                      <AcademicPanelListItem key={row.id}>
                        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
                          <div className="flex min-w-0 items-center gap-3">
                            {row.urlLogo ? (
                              <img
                                src={row.urlLogo}
                                alt=""
                                className="h-10 w-10 shrink-0 rounded-lg object-contain ring-1 ring-outline-variant/20"
                              />
                            ) : (
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                                <Handshake className="h-4 w-4" aria-hidden />
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-bold text-slate-900">{row.nom}</p>
                                <span
                                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                    inactiveLink
                                      ? 'bg-slate-200 text-slate-700'
                                      : 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200/80'
                                  }`}
                                >
                                  {inactiveLink ? 'Lien inactif' : 'Lié'}
                                </span>
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                                  {categorieLabel}
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-on-surface-variant">
                                {row.nbOpportunites ?? 0} opportunité(s)
                                {row.nbOpportunitesActives != null
                                  ? ` · ${row.nbOpportunitesActives} active(s)`
                                  : ''}
                                {oppTitles ? ` · ${oppTitles}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/admin/partenaires-sponsors/${row.id}`}
                              className={`${BTN_SECONDARY} !px-3 !py-1.5 text-xs`}
                            >
                              Voir la fiche
                            </Link>
                            {!inactiveLink ? (
                              <button
                                type="button"
                                disabled={detachingPartenaireId === row.id}
                                className={`${BTN_SECONDARY} !px-3 !py-1.5 text-xs disabled:opacity-50`}
                                onClick={() => {
                                  setDetachingPartenaireId(row.id);
                                  void runAction(
                                    () => setExcellencePartenaireLien(row.id, { lienActif: false }),
                                    'Partenaire détaché des opportunités.',
                                    'Désactivation impossible.',
                                  ).finally(() => setDetachingPartenaireId(null));
                                }}
                              >
                                {detachingPartenaireId === row.id ? '…' : 'Désactiver le lien'}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </AcademicPanelListItem>
                    );
                  })}
                </ul>
              </AcademicPanelTableWrap>
            </AcademicPanelCard>
          ) : null}
        </div>
      ) : null}

      {editModalOpp ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-[2px]"
          onClick={closeEditModal}
          role="presentation"
        >
          <div
            className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-outline-variant/25 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-opportunite-title"
          >
            <header className="flex items-start justify-between gap-4 border-b border-outline-variant/15 bg-gradient-to-r from-primary/5 to-white px-5 py-4">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">Modification</p>
                <h3 id="edit-opportunite-title" className="mt-1 truncate text-lg font-black text-slate-900">
                  {editModalOpp.titre}
                </h3>
                <p className="mt-1 text-xs text-on-surface-variant">
                  Mettez à jour les informations sans quitter la liste des opportunités.
                </p>
              </div>
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-outline-variant/25 text-slate-600 transition hover:bg-slate-50"
                onClick={closeEditModal}
                aria-label="Fermer la fenêtre de modification"
              >
                <LucideSymbolIcon name="close" tone="inherit" size={18} />
              </button>
            </header>

            <div className="overflow-y-auto px-5 py-5">
              <OpportuniteFormFields form={editForm} setForm={setEditForm} partenaires={partnerOptionsForForm} />
            </div>

            <footer className="flex flex-wrap justify-end gap-2 border-t border-outline-variant/15 bg-slate-50/80 px-5 py-4">
              <button type="button" className={BTN_SECONDARY} onClick={closeEditModal}>
                Annuler
              </button>
              <button
                type="button"
                disabled={savingEditOpp || !editForm.titre.trim() || !editForm.description.trim()}
                className={`${BTN_PRIMARY} disabled:opacity-50`}
                onClick={async () => {
                  setSavingEditOpp(true);
                  try {
                    await runAction(
                      async () => {
                        await saveExcellenceOpportunite(
                          {
                            ...editForm,
                            periode,
                            idPartenaire: editForm.idPartenaire || null,
                            estActif: editModalOpp.estActif !== false,
                          },
                          editModalOpp.id,
                        );
                        closeEditModal();
                      },
                      'Opportunité mise à jour.',
                      'Mise à jour impossible.',
                    );
                  } finally {
                    setSavingEditOpp(false);
                  }
                }}
              >
                {savingEditOpp ? 'Enregistrement…' : 'Enregistrer les modifications'}
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {editModalPeriode ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-[2px]"
          onClick={closeEditPeriodeModal}
          role="presentation"
        >
          <div
            className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-outline-variant/25 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-periode-title"
          >
            <header className="flex items-start justify-between gap-4 border-b border-outline-variant/15 bg-gradient-to-r from-primary/5 to-white px-5 py-4">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">Modification</p>
                <h3 id="edit-periode-title" className="mt-1 truncate text-lg font-black text-slate-900">
                  {formatPeriodeLabel(editModalPeriode.code)}
                </h3>
              </div>
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-outline-variant/25 text-slate-600 transition hover:bg-slate-50"
                onClick={closeEditPeriodeModal}
                aria-label="Fermer"
              >
                <LucideSymbolIcon name="close" tone="inherit" size={18} />
              </button>
            </header>

            <div className="overflow-y-auto px-5 py-5">
              <PeriodeFormFields
                form={editPeriodeForm}
                setForm={setEditPeriodeForm}
                allowStatusFields
              />
            </div>

            <footer className="flex flex-wrap justify-end gap-2 border-t border-outline-variant/15 bg-slate-50/80 px-5 py-4">
              <button type="button" className={BTN_SECONDARY} onClick={closeEditPeriodeModal}>
                Annuler
              </button>
              <button
                type="button"
                disabled={savingEditPeriode}
                className={`${BTN_PRIMARY} disabled:opacity-50`}
                onClick={async () => {
                  setSavingEditPeriode(true);
                  try {
                    const newCode = buildPeriodeCode(
                      editPeriodeForm.year,
                      Number(editPeriodeForm.semester) as 1 | 2,
                    );
                    await runAction(
                      async () => {
                        await saveExcellencePeriode(
                          {
                            libelle: editPeriodeForm.libelle || null,
                            estActive: editPeriodeForm.estActive,
                            estCourante: editPeriodeForm.estCourante,
                            newCode: newCode !== editModalPeriode.code ? newCode : undefined,
                          },
                          editModalPeriode.code,
                        );
                        if (periode === editModalPeriode.code && newCode !== editModalPeriode.code) {
                          setPeriode(newCode);
                        }
                        closeEditPeriodeModal();
                      },
                      'Période mise à jour.',
                      'Mise à jour impossible.',
                    );
                  } finally {
                    setSavingEditPeriode(false);
                  }
                }}
              >
                {savingEditPeriode ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
    </AdminPageShell>
  );
}
